import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Link } from 'react-router-dom';
import { Star, MapPin, Zap, CalendarDays } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { getInitials, formatCurrency, getTrainerPriceForCategory, CATEGORY_NAMES } from '../lib/utils';
import BookingDialog from './BookingDialog';

// Build pin icons, gold (offline/available) + green (available now)
const makePin = (fillColor, ringColor) => L.divIcon({
  className: 'doorguard-trainer-pin',
  html: `
    <svg width="32" height="44" viewBox="0 0 32 44" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="pinshadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#000" flood-opacity="0.45"/>
        </filter>
      </defs>
      <path filter="url(#pinshadow)" d="M16 1.5C8 1.5 1.5 8 1.5 16c0 9.5 12 24.5 13.5 26.4.5.7 1.5.7 2 0C18.5 40.5 30.5 25.5 30.5 16 30.5 8 24 1.5 16 1.5z"
            fill="${fillColor}" stroke="${ringColor}" stroke-width="2"/>
      <circle cx="16" cy="16" r="5.5" fill="#0d0d10"/>
    </svg>
  `,
  iconSize: [32, 44],
  iconAnchor: [16, 42],
  popupAnchor: [0, -38],
});

const goldPinIcon = makePin('#C0A062', '#0d0d10');         // offline, schedulable
const greenPinIcon = makePin('#10b981', '#052e1f');        // available now
const greyPinIcon = makePin('#52525b', '#27272a');         // no schedule / cannot book

const FitToTrainers = ({ trainers, userCoords }) => {
  const map = useMap();
  useEffect(() => {
    const pts = trainers.filter((t) => t.lat && t.lng).map((t) => [t.lat, t.lng]);
    if (userCoords) pts.push([userCoords.lat, userCoords.lng]);
    if (pts.length === 0) return;
    if (pts.length === 1) {
      map.setView(pts[0], 11);
    } else {
      map.fitBounds(pts, { padding: [40, 40], maxZoom: 12 });
    }
  }, [map, trainers, userCoords]);
  return null;
};

const TrainerPopupCard = ({ trainer, onBookNow, onSchedule }) => {
  const profile = trainer.trainer_profile || {};
  const offerings = [
    { key: 'personal_training', label: CATEGORY_NAMES.personal_training, price: getTrainerPriceForCategory(profile, 'personal_training') },
    { key: 'self_defense', label: CATEGORY_NAMES.self_defense, price: getTrainerPriceForCategory(profile, 'self_defense') },
    { key: 'jiujitsu', label: CATEGORY_NAMES.jiujitsu, price: getTrainerPriceForCategory(profile, 'jiujitsu') },
  ].filter((o) => o.price && o.price > 0);

  const startingPrice = offerings.length ? Math.min(...offerings.map((o) => o.price)) : null;
  const isAvailableNow = !!trainer.is_available_now;
  const canBook = offerings.length > 0;

  return (
    <div className="w-[260px]" data-testid={`map-popup-${trainer.id}`}>
      <div className="flex items-center gap-3 mb-2">
        <Avatar className="w-11 h-11 border border-[#C0A062]/30">
          <AvatarImage src={profile.profile_image} alt={trainer.full_name} />
          <AvatarFallback className="bg-[#27272A] text-[#C0A062] text-xs font-semibold">
            {getInitials(trainer.full_name || 'T')}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-zinc-900 text-sm truncate leading-tight">{trainer.full_name}</p>
          <div className="flex items-center gap-1 mt-0.5 text-xs text-zinc-700">
            <Star className="w-3 h-3 fill-[#C0A062] text-[#C0A062]" />
            <span className="font-medium">{(trainer.avg_rating || 0).toFixed(1)}</span>
            <span className="text-zinc-500">· {trainer.review_count || 0}</span>
          </div>
        </div>
      </div>

      {isAvailableNow && (
        <div className="mb-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 border border-emerald-500/30 text-[10px] font-bold uppercase tracking-wider" data-testid={`map-available-${trainer.id}`}>
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Available now
        </div>
      )}

      {offerings.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {offerings.map((o) => (
            <span key={o.key} className="text-[10px] uppercase tracking-wider font-semibold bg-zinc-200 text-zinc-800 rounded px-1.5 py-0.5">
              {o.label}
            </span>
          ))}
        </div>
      )}

      {startingPrice && (
        <p className="text-xs text-zinc-700 mb-3">
          From <span className="font-bold text-zinc-900">{formatCurrency(startingPrice)}</span>{' '}
          <span className="text-zinc-500">/ session</span>
        </p>
      )}

      <div className="space-y-1.5">
        {isAvailableNow && canBook && (
          <button
            type="button"
            onClick={() => onBookNow(trainer)}
            className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-bold bg-emerald-500 text-black hover:bg-emerald-600 transition-colors rounded-md px-3 py-2"
            data-testid={`map-book-now-${trainer.id}`}
          >
            <Zap className="w-3.5 h-3.5" />
            Book Now
          </button>
        )}
        {canBook && (
          <button
            type="button"
            onClick={() => onSchedule(trainer)}
            className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold bg-[#0d0d10] text-[#C0A062] hover:bg-zinc-800 transition-colors rounded-md px-3 py-2"
            data-testid={`map-schedule-${trainer.id}`}
          >
            <CalendarDays className="w-3.5 h-3.5" />
            Schedule a Session
          </button>
        )}
        <Link
          to={`/trainers/${trainer.id}`}
          className="block text-center text-[11px] text-zinc-600 hover:text-zinc-900 underline"
          data-testid={`map-view-profile-${trainer.id}`}
        >
          View full profile
        </Link>
      </div>
    </div>
  );
};

// Continental US fallback center
const US_CENTER = { lat: 39.8283, lng: -98.5795 };

const TrainerMap = ({ trainers, onPinClick, heightClass = 'h-[600px]' }) => {
  const [userCoords, setUserCoords] = useState(null);
  const [bookingTrainer, setBookingTrainer] = useState(null);
  const [bookingType, setBookingType] = useState('scheduled');

  useEffect(() => {
    if (!('geolocation' in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: false, timeout: 4000, maximumAge: 60_000 }
    );
  }, []);

  const mappable = useMemo(
    () => (trainers || []).filter((t) => typeof t.trainer_profile?.lat === 'number' && typeof t.trainer_profile?.lng === 'number')
      .map((t) => ({ ...t, lat: t.trainer_profile.lat, lng: t.trainer_profile.lng })),
    [trainers]
  );

  const center = userCoords || (mappable.length ? { lat: mappable[0].lat, lng: mappable[0].lng } : US_CENTER);

  const pickIcon = (t) => {
    if (t.is_available_now) return greenPinIcon;
    const profile = t.trainer_profile || {};
    const hasSchedule = Object.values(profile.weekly_schedule || {}).some((d) => d?.enabled);
    return hasSchedule ? goldPinIcon : greyPinIcon;
  };

  const openBookNow = (t) => { setBookingTrainer(t); setBookingType('instant'); };
  const openSchedule = (t) => { setBookingTrainer(t); setBookingType('scheduled'); };

  return (
    <div className={`relative w-full ${heightClass} rounded-lg overflow-hidden border border-zinc-800 isolate`} data-testid="trainer-map">
      {mappable.length === 0 && (
        <div className="absolute z-[1000] top-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-[#0d0d10]/95 border border-zinc-700 text-sm text-zinc-300 shadow-lg flex items-center gap-2" data-testid="trainer-map-empty">
          <MapPin className="w-4 h-4 text-[#C0A062]" />
          No trainers with locations set yet, try List view.
        </div>
      )}
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={userCoords ? 10 : 4}
        scrollWheelZoom
        style={{ height: '100%', width: '100%', background: '#09090B' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitToTrainers trainers={mappable} userCoords={userCoords} />
        {mappable.map((t) => (
          <Marker key={t.id} position={[t.lat, t.lng]} icon={pickIcon(t)} eventHandlers={{ click: () => onPinClick?.(t.id) }}>
            <Popup>
              <TrainerPopupCard trainer={t} onBookNow={openBookNow} onSchedule={openSchedule} />
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      <BookingDialog
        open={!!bookingTrainer}
        onOpenChange={(o) => { if (!o) setBookingTrainer(null); }}
        trainer={bookingTrainer}
        bookingType={bookingType}
      />
    </div>
  );
};

export default TrainerMap;
